import { useMemo, useState } from "react";
import { SortingState, GroupingState } from "@tanstack/react-table";

import { Breadcrumbs } from "ui/breadcrumbs";
import { TableLoader, DataGrid, TableSearchAndFilters, TableContainer } from "ui/data-grid";
import BatchTableRow from "containers/client/reviews/components/batch-table-row/batch-table-row";

import { useFeatureFlag } from "contexts/feature-flag-context";
import { useCurrentUser } from "hooks/api/use-current-user";
import { useInboxReviews } from "hooks/api/use-inbox-reviews";
import {
  useGetAudienceTypesQuery,
  useGetTouchpointVersionStatusesQuery,
} from "hooks/api/dictionary";

import { createReviewsTableColumns } from "containers/client/reviews/touchpoints/touchpoints-table-columns";
import FiltersOnClientReviewsTable from "containers/client/components/client-reviews-table/filters-on-client-reviews-table";
import {
  CLIENT_REVIEWS_FILTER_ID_TO_TITLE,
  CLIENT_REVIEWS_FILTER_VALUE_TO_LABEL,
  CLIENT_REVIEWS_SEARCH_PLACEHOLDER,
  CLIENT_TABLE_CONFIG_KEYS,
  DEFAULT_CLIENT_REVIEWS_FILTERS,
} from "containers/client/components/client-reviews-table/client-reviews-table.consts";
import { useClientTableColumnLayout } from "containers/client/hooks/use-client-table-column-layout";

import { InboxFilter } from "types/inbox";
import { Route as AppRoute } from "utilities/app-routes";
import { PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";
import { TouchpointStatuses } from "types";
import { FiltersToApplyOnClientReviewsTable } from "containers/client/components/client-reviews-table/client-reviews-table.types";
import { TouchpointVersionHistoryDto } from "generated/schemas";

const TOUCHPOINT_BATCH_SORTS = [
  "touchpointBatchDueDate,asc,nullslast",
  "touchpointBatchId,asc,nullslast",
] as const;

const TOUCHPOINT_VERSION_ID_TIEBREAKER = "touchpointVersionId,asc";

const buildReviewsSortQuery = (
  sortingParams: SortingState,
  isBatchManagementEnabled: boolean,
): string => {
  const userSorts = sortingParams
    .filter((param) => param.id !== "touchpointVersionId")
    .map((param) => `${param.id},${param.desc ? "desc" : "asc"}`);

  if (!isBatchManagementEnabled) {
    return userSorts.join("&sort=");
  }

  const sorts = [...TOUCHPOINT_BATCH_SORTS, ...userSorts, TOUCHPOINT_VERSION_ID_TIEBREAKER];

  return sorts.join("&sort=");
};

const Touchpoints = () => {
  const isBatchManagementEnabled = useFeatureFlag("batch-management");
  const [filtersToApply, setFiltersToApply] = useState<FiltersToApplyOnClientReviewsTable>(
    DEFAULT_CLIENT_REVIEWS_FILTERS,
  );
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>("");
  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: "lastModifiedDate", desc: true },
  ]);

  const grouping: GroupingState = ["touchpointBatchId"];

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const handleSortingParamsChange = (next: SortingState) => {
    setSortingParams(next);
    setPageIndex(0);
  };

  const handleAmountChange = ({ pageSize: nextPageSize }: { pageSize: number }) => {
    setPageSize(nextPageSize);
    setPageIndex(0);
  };

  const { data: audienceDictionaryItems = [] } = useGetAudienceTypesQuery();
  const { data: touchpointVersionStatuses = [] } = useGetTouchpointVersionStatusesQuery();

  const handleSearchTermChange = (searchTerm: string) => {
    setPageIndex(0);
    setCurrentSearchTerm(searchTerm);
  };

  const handleFiltersChange = (next: FiltersToApplyOnClientReviewsTable) => {
    setFiltersToApply(next);
    setPageIndex(0);
  };

  const inboxFilters: InboxFilter = {
    touchpointVersionStatus: [TouchpointStatuses.CLIENT_REVIEW],
    touchpointType: filtersToApply.type?.length ? filtersToApply.type : [],
    populationType: filtersToApply.populationType?.length ? filtersToApply.populationType : [],
    q: currentSearchTerm,
  };

  const { data: reviewsData, isLoading } = useInboxReviews({
    page: pageIndex,
    size: pageSize,
    sort: buildReviewsSortQuery(sortingParams, isBatchManagementEnabled),
    filters: inboxFilters,
  });

  const currentSearchResults = reviewsData?.items ?? [];
  const totalRecords = reviewsData?.totalElements ?? 0;
  const pagesAmount = reviewsData?.totalPages ?? 0;

  const isSearchOrFiltering = Boolean(
    currentSearchTerm || filtersToApply.type?.length || filtersToApply.populationType?.length,
  );

  const allColumns = useMemo(
    () =>
      createReviewsTableColumns({
        audienceDictionaryItems,
        touchpointVersionStatuses,
        isBatchManagementEnabled,
        hasTemplates: currentSearchResults.some(
          (tp) => tp.campaignName === PROGRAM_TEMPLATES_CAMPAIGN_NAME,
        ),
      }),
    [
      audienceDictionaryItems,
      touchpointVersionStatuses,
      isBatchManagementEnabled,
      currentSearchResults,
    ],
  );

  const { tableColumnsConfig, columnOptions, visibleColumns, handleToggleColumn } =
    useClientTableColumnLayout({
      tableConfigKey: CLIENT_TABLE_CONFIG_KEYS.reviewsCreatives,
      allColumns,
      primaryColumnIds: ["touchpointName"],
    });

  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-hidden bg-gray-100">
      <div className="shrink-0 pt-3 pl-3 pr-5 pb-4">
        <div className="mb-4 flex h-5 items-center">
          <Breadcrumbs
            breadcrumbs={[{ label: "Reviews", href: AppRoute.reviews }, { label: "Creative" }]}
          />
        </div>
        <TableSearchAndFilters
          setCurrentSearchTerm={handleSearchTermChange}
          placeholder={CLIENT_REVIEWS_SEARCH_PLACEHOLDER}
          filtersToApply={filtersToApply}
          setFiltersToApply={handleFiltersChange}
          defaultFiltersToApply={DEFAULT_CLIENT_REVIEWS_FILTERS}
          filterIdToTitle={CLIENT_REVIEWS_FILTER_ID_TO_TITLE}
          filterValueToLabel={CLIENT_REVIEWS_FILTER_VALUE_TO_LABEL}
          tableFilters={
            <FiltersOnClientReviewsTable
              filtersToApply={filtersToApply}
              setFiltersToApply={handleFiltersChange}
              tableConfigKey={CLIENT_TABLE_CONFIG_KEYS.reviewsCreatives}
              tableColumnsConfig={tableColumnsConfig}
              columnOptions={columnOptions}
              onToggleColumn={handleToggleColumn}
              isFetching={isLoading}
            />
          }
        />
      </div>

      <div className="min-h-0 flex-1 w-full overflow-auto custom-scroll">
        <div className="pl-3 pr-5 pb-5">
          <TableContainer className="mb-0">
            <TableLoader isLoading={isLoading} fullScreen />

            {!isLoading &&
              (currentSearchResults.length ? (
                <DataGrid
                  columns={visibleColumns}
                  data={currentSearchResults}
                  isLoading={isLoading}
                  styles={{ containerClassName: "custom-scroll" }}
                  manualSorting={true}
                  showingRecordsIsOn={true}
                  viewByAmountSelectIsOn={true}
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  pagesAmount={pagesAmount}
                  setAmount={handleAmountChange}
                  pageIndex={pageIndex}
                  setPageIndex={(pageIndex) => setPageIndex(pageIndex)}
                  sortingParams={sortingParams}
                  setSortingParams={handleSortingParamsChange}
                  isExpandable={true}
                  // Grouping hidden under feature flag
                  enableGrouping={isBatchManagementEnabled}
                  grouping={isBatchManagementEnabled ? grouping : undefined}
                  groupedColumnMode={isBatchManagementEnabled ? "remove" : undefined}
                  getGroupedNode={(row) => <BatchTableRow row={row} />}
                />
              ) : isSearchOrFiltering ? (
                <p className="text-gray-500">No results found</p>
              ) : (
                <p className="text-gray-500">There are no creatives to review yet</p>
              ))}
          </TableContainer>
        </div>
      </div>
    </div>
  );
};

export default Touchpoints;
